import React from 'react';

import { IconButton } from 'components';
import { useTranslation } from 'react-i18next';

import {
  AddProductToChargeDetails,
  TaxAmountType,
} from 'application-types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';

import { M, Column, Icon, Tooltip } from 'design-system-web';

import styles from '../addProductToChargeStyles.scss';
import { createChargeLineTableCellRenderers } from '../../helpers/chargeLineTableCellRenderers';

export function useTableColumnsAddedProducts({
  currency,
  removeProduct,
  editProduct,
}: {
  currency: SupportedPaymentCurrency;
  removeProduct: Function;
  editProduct: Function;
}): Column<AddProductToChargeDetails>[] {
  const { t } = useTranslation();
  const isAdminFeeEnabled = useBpAdminFeeChargeCodeEnabled();

  const { renderFixedAmountCell, renderRateModifierCell, renderAdminFeeTaxCell } =
    createChargeLineTableCellRenderers({ currency, t });

  const renderProductTypeCell = (cell: string) => {
    return t(`abp.productsServices.productType.${cell}`);
  };

  const renderIconCell = (
    data: AddProductToChargeDetails,
    type: 'delete' | 'edit'
  ) => {
    const iconToUse = type === 'delete' ? 'trash-alt' : 'pen';
    const iconFunction = type === 'delete' ? removeProduct : editProduct;
    const icontestId =
      type === 'delete' ? 'deleteIconButton' : 'editIconButton';

    return (
      <IconButton
        icon={iconToUse}
        onClick={() => iconFunction(data)}
        testId={icontestId}
      />
    );
  };

  const renderTotalCell = (cell: string, row: AddProductToChargeDetails) => {
    return (
      <div
        className={styles.totalCell}
        data-test={`totalCell-${row.productId}`}
      >
        <M>{renderFixedAmountCell(cell)}</M>
        {row.originalPrice && (
          <Tooltip
            TooltipComponent={t('abp.charges.addProduct.modal.exceptionInfo')}
            zIndex={100}
            placement="right"
          >
            <Icon
              name="exclamation-circle"
              set="FontAwesome"
              type="far"
              size="medium"
            />
          </Tooltip>
        )}
      </div>
    );
  };

  return [
    {
      key: 'productCategoryType',
      header: t('abp.charges.addProduct.table.headers.type'),
      renderCell: renderProductTypeCell,
    },
    {
      key: 'productCategoryName',
      header: t('abp.charges.addProduct.table.headers.category'),
      renderCell: renderStringCell,
    },
    {
      key: 'chargeCode',
      header: t('abp.charges.addProduct.table.headers.chargeCode'),
      renderCell: renderStringCell,
    },
    {
      key: 'name',
      header: t('abp.charges.addProduct.table.headers.item'),
      renderCell: renderStringCell,
    },
    {
      key: 'rate',
      header: t('abp.charges.addProduct.table.headers.rate'),
      renderCell: renderFixedAmountCell,
    },
    {
      key: 'quantity',
      header: t('abp.charges.addProduct.table.headers.qty'),
      renderCell: renderStringCell,
    },
    {
      key: 'markup',
      header: t('abp.charges.addProduct.table.headers.markup'),
      renderCell: renderRateModifierCell,
    },
    ...(isAdminFeeEnabled
      ? [
          {
            key: 'adminFee',
            header: t('abp.charges.addProduct.table.headers.admin'),
            renderCell: renderRateModifierCell,
          },
          {
            key: 'adminFeeTax',
            header: t('abp.charges.addProduct.table.headers.adminTax'),
            renderCell: (
              cell: { value: string; type: TaxAmountType },
              row: AddProductToChargeDetails
            ) => renderAdminFeeTaxCell(cell, row),
          },
        ]
      : []),
    {
      key: 'tax',
      header: t('abp.charges.addProduct.table.headers.tax'),
      renderCell: renderRateModifierCell,
    },
    {
      key: 'total',
      header: t('abp.charges.addProduct.table.headers.total'),
      renderCell: (cell: string, row: AddProductToChargeDetails) =>
        renderTotalCell(cell, row),
    },
    {
      key: 'delete',
      header: '',
      renderCell: (_: unknown, row: AddProductToChargeDetails) =>
        renderIconCell(row, 'delete'),
    },
    {
      key: 'edit',
      header: '',
      renderCell: (_: unknown, row: AddProductToChargeDetails) =>
        renderIconCell(row, 'edit'),
    },
  ];
}

function renderStringCell(cell: string) {
  return cell;
}
